import { type LegendListRef } from '@legendapp/list/react-native'
import { useScrollToTop } from '@react-navigation/native'
import { useFocusEffect } from 'expo-router'
import { useCallback, useRef, useState, type Dispatch, type ReactNode, type SetStateAction } from 'react'
import { FlatList, ScrollView } from 'react-native'

import {
  BrandGradientView,
  ScreenFooter,
  ScreenScrollView,
  type LoadingViewProps,
  type ScreenFooterProps,
  type ScreenScrollViewProps,
  type VStackProps
} from '@/components'

export type ScreenScrollable = ScrollView | FlatList<any> | LegendListRef

export type ScreenRenderProps = {
  scrollEnabled: boolean
  setScrollEnabled: Dispatch<SetStateAction<boolean>>
  setScrollRef: (node: ScreenScrollable | null) => void
}

export interface ScreenProps extends Omit<
  VStackProps,
  'accessibilityElementsHidden' | 'aria-hidden' | 'children' | 'importantForAccessibility'
> {
  children: ReactNode | ((context: ScreenRenderProps) => ReactNode)
  contentClassName?: ScreenScrollViewProps['contentContainerClassName']
  footer?: ReactNode
  footerClassName?: ScreenFooterProps['className']
  loadingViewProps?: LoadingViewProps
  vstackProps?: VStackProps
  withCenterHorizontal?: boolean
  withCenterVertical?: boolean
  withGap?: boolean
  withPadding?: boolean
  withScrollView?: boolean
}

export const Screen = ({
  children,
  className,
  contentClassName,
  footer,
  footerClassName,
  loadingViewProps,
  withCenterHorizontal,
  withCenterVertical,
  withGap = true,
  withPadding = true,
  withScrollView = true,
  ...props
}: ScreenProps) => {
  const [scrollEnabled, setScrollEnabled] = useState(true)
  const scrollRef = useRef<ScreenScrollable>(null)
  const setScrollRef = useCallback((node: ScreenScrollable | null) => {
    scrollRef.current = node
  }, [])

  useScrollToTop(scrollRef)
  useFocusEffect(
    useCallback(() => {
      const timeout = setTimeout(() => {
        const node = scrollRef.current as ScreenScrollable

        if (node && 'scrollTo' in node) {
          node.scrollTo({
            animated: false,
            y: 0
          })
        }
      }, 0)

      return () => {
        clearTimeout(timeout)
      }
    }, [])
  )

  return (
    <BrandGradientView {...props}>
      {typeof children === 'function' ? (
        children({ scrollEnabled, setScrollEnabled, setScrollRef })
      ) : withScrollView ? (
        <>
          <ScreenScrollView
            contentContainerClassName={contentClassName}
            ref={setScrollRef}
            scrollEnabled={scrollEnabled}
            withCenterHorizontal={withCenterHorizontal}
            withCenterVertical={withCenterVertical}
            withGap={withGap}
            withPadding={withPadding}
          >
            {children}
          </ScreenScrollView>
          {footer ? <ScreenFooter className={footerClassName}>{footer}</ScreenFooter> : null}
        </>
      ) : (
        children
      )}
    </BrandGradientView>
  )
}
